import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import BatchPagination from '../../batch/BatchPagination';
import AlertDialogModal from '../../common/AlertDialog';
import LoaderSpinner from '../../common/Loader';
import { numOfPage, deleteIconRed } from '../../../constant';
import { useInvitationReferrals } from '../Hooks/AffiliateManagement.hooks';
import OnboardingService from '../../../services/onboarding.service';
import {
    InvitationStatusPill,
    INVITATION_STATUS_PENDING as STATUS_PENDING,
    INVITATION_STATUS_APPROVED as STATUS_APPROVED,
    INVITATION_STATUS_NOT_SIGNED_UP as STATUS_NOT_SIGNED_UP,
} from '../utils/invitationStatus';

const ReferralStatusCell = ({ item }) => {
    const status = item?.Status || STATUS_NOT_SIGNED_UP;
    return (
        <div>
            <InvitationStatusPill status={status} />
            {status === STATUS_APPROVED && item?.ApprovedCompanySellerName && (
                <div
                    className="text-muted small mt-1"
                    style={{ wordBreak: 'break-word' }}
                >
                    {item.ApprovedCompanySellerName}
                </div>
            )}
        </div>
    );
};

ReferralStatusCell.propTypes = {
    item: PropTypes.object.isRequired,
};

// Email cell: clickable when the referred user is navigable (Pending/Approved),
// plain text otherwise.
const ReferralEmailCell = ({ item, onNavigatePending, onNavigateApproved }) => {
    const status = item?.Status;
    const email = item?.InviteeEmail || 'N/A';

    if (status === STATUS_PENDING) {
        return (
            <button
                type="button"
                className="btn btn-link p-0 text-left"
                onClick={() => onNavigatePending(item)}
                title="Open in User Requests"
                style={{ wordBreak: 'break-word', whiteSpace: 'normal', textAlign: 'left' }}
            >
                {email}
            </button>
        );
    }

    if (status === STATUS_APPROVED) {
        return (
            <button
                type="button"
                className="btn btn-link p-0 text-left"
                onClick={() => onNavigateApproved(item)}
                title="Open in Company Management"
                style={{ wordBreak: 'break-word', whiteSpace: 'normal', textAlign: 'left' }}
            >
                {email}
            </button>
        );
    }

    return <span style={{ wordBreak: 'break-word' }}>{email}</span>;
};

ReferralEmailCell.propTypes = {
    item: PropTypes.object.isRequired,
    onNavigatePending: PropTypes.func.isRequired,
    onNavigateApproved: PropTypes.func.isRequired,
};

const InvitationReferralsModal = ({ show, onHide, companySeller, onOuterRefresh }) => {
    const navigate = useNavigate();
    const companySellerId = companySeller?.CompanySellerId;
    const companySellerName = companySeller?.CompanySellerName || '';

    const {
        referralData,
        loading,
        totalRecords,
        currentPageIndex,
        setCurrentPageIndex,
        pageSize,
        setPageSize,
        refresh,
    } = useInvitationReferrals(companySellerId, show);

    const [pendingDelete, setPendingDelete] = useState(null);
    const [deleteLoader, setDeleteLoader] = useState(false);

    const handleCopy = async (item) => {
        const url = item?.InvitationUrl;
        if (!url) {
            toast.error('No URL available to copy');
            return;
        }
        try {
            await navigator.clipboard.writeText(url);
            toast.success('Invitation URL Copied');
        } catch {
            toast.error('Failed to copy URL');
        }
    };

    const handleNavigatePending = (item) => {
        if (!item?.PendingSignupId) return;
        onHide();
        navigate('/user-request', {
            state: { from: 'partner-management', pendingSignupId: item.PendingSignupId },
        });
    };

    const handleNavigateApproved = (item) => {
        const companyId = item?.ApprovedCompanyId;
        if (!companyId) return;
        onHide();
        navigate(`/editCompany/comp-${companyId}`, {
            state: { from: 'partner-management' },
        });
    };

    const handleConfirmDelete = async () => {
        if (!pendingDelete) return;
        setDeleteLoader(true);
        try {
            const response = await OnboardingService.deleteInvitation(pendingDelete.InvitationId);
            if (response?.data?.Status === 200) {
                toast.success('Invitation deleted successfully');
                setPendingDelete(null);
                refresh();
                if (typeof onOuterRefresh === 'function') onOuterRefresh();
            } else {
                toast.error(response?.data?.Message?.AppStatusDescription || 'Failed to delete invitation');
            }
        } catch (error) {
            toast.error(error?.response?.data?.errorDescription || 'Error deleting invitation');
        } finally {
            setDeleteLoader(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
    const startIdx = (currentPageIndex - 1) * pageSize;

    return (
        <>
            <Modal
                show={show}
                onHide={onHide}
                size="xl"
                aria-labelledby="invitation-referrals-modal"
                centered
                className="filterShippingListModal modalDeleteWrapper generateKey"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="invitation-referrals-modal">
                        Referred Users {companySellerName ? `— ${companySellerName}` : ''}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="company-popup-section">
                        <div className="tableFixHead incomingTbl company-popup-table-wrapper">
                            {loading && <LoaderSpinner />}
                            {!loading && (
                                <Table className="table table-height cus-left-right-col-layout affiliateTable">
                                    <thead>
                                        <tr>
                                            <th className="trows-s">#</th>
                                            <th style={{ minWidth: 220, maxWidth: 260 }}>Email</th>
                                            <th style={{ minWidth: 130 }}>Created At</th>
                                            <th>URL</th>
                                            <th style={{ minWidth: 180, maxWidth: 260 }}>Status</th>
                                            <th className="trows-s text-center">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {referralData.length === 0 && (
                                            <tr>
                                                <td colSpan={6} align="center">No Record Found</td>
                                            </tr>
                                        )}
                                        {referralData.map((item, index) => (
                                            <tr key={item.InvitationId}>
                                                <td className="trows-s">{startIdx + index + 1}</td>
                                                <td style={{ minWidth: 220, maxWidth: 260, wordBreak: 'break-word' }}>
                                                    <ReferralEmailCell
                                                        item={item}
                                                        onNavigatePending={handleNavigatePending}
                                                        onNavigateApproved={handleNavigateApproved}
                                                    />
                                                </td>
                                                <td style={{ minWidth: 130 }}>{item.CreatedDate ? moment(item.CreatedDate).format('MM-DD-YYYY HH:mm') : 'N/A'}</td>
                                                <td>
                                                    {item.InvitationUrl ? (
                                                        <div className="keyFieldWrapper">
                                                            <input
                                                                value={item.InvitationUrl}
                                                                className="form-control"
                                                                readOnly
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => handleCopy(item)}
                                                                className="btn copyBtn"
                                                                title="Copy URL"
                                                            >
                                                                <i className="fa fa-clone" aria-hidden="true"></i>
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        'N/A'
                                                    )}
                                                </td>
                                                <td style={{ minWidth: 180, maxWidth: 260 }}>
                                                    <ReferralStatusCell item={item} />
                                                </td>
                                                <td className="trows-s text-center">
                                                    <button
                                                        type="button"
                                                        className="btn btn-link p-1 sortBtn"
                                                        onClick={() => setPendingDelete(item)}
                                                        title="Delete Invitation"
                                                        aria-label="Delete invitation"
                                                    >
                                                        {deleteIconRed()}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </div>
                        {referralData.length > 0 && (
                            <BatchPagination
                                gridData={referralData}
                                numberOfPage={pageSize}
                                numOfPage={numOfPage}
                                loader={false}
                                currentPage={currentPageIndex}
                                totalPages={totalPages}
                                setCurrentPage={setCurrentPageIndex}
                                totalRecords={totalRecords}
                                setNumberOfPage={(val) => { setPageSize(val); setCurrentPageIndex(1); }}
                            />
                        )}
                    </div>
                </Modal.Body>
            </Modal>

            <AlertDialogModal
                modalClass={'modalDeleteWrapper'}
                modalShow={!!pendingDelete}
                headingText={'Delete Invitation'}
                messageText={'Are you sure you want to delete this invitation?'}
                handleCancelDeleteAlert={() => !deleteLoader && setPendingDelete(null)}
                handleYesDeleteAlert={handleConfirmDelete}
                loader={deleteLoader}
            />
        </>
    );
};

InvitationReferralsModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    companySeller: PropTypes.shape({
        CompanySellerId: PropTypes.number,
        CompanySellerName: PropTypes.string,
    }),
    onOuterRefresh: PropTypes.func,
};

export default InvitationReferralsModal;
